import i18n from '../i18n';
import { refreshToken as refreshAuthToken } from '../services/auth-service';
import { safeParseJSON } from './api-utils';
import { getApiBaseUrl } from './api-base-url';

// Chiavi per lo storage locale dei token
const TOKEN_KEY = 'climb_log_auth_token';
const REFRESH_TOKEN_KEY = 'climb_log_refresh_token';

// Interfaces for compatibility with different API response formats
interface ApiResponse<T> {
  data?: T;
  error?: string;
}

// Global configuration for API client
interface ApiClientConfig {
  baseURL: string;
  timeout?: number;
  showErrors?: boolean; // Flag to enable/disable automatic error display
}

// JSON:API error format
interface JsonApiError {
  status: string;
  title: string;
  code: string;
  detail: string;
}

/**
 * Extract error message from JSON:API error response
 * @param errorData The parsed error response data
 */
function extractErrorMessage(errorData: unknown): string {
  const data = errorData as Record<string, unknown>;
  // Check if it's a JSON:API error format
  if (data.errors && Array.isArray(data.errors) && data.errors.length > 0) {
    const firstError = data.errors[0] as JsonApiError;
    return firstError.detail || firstError.title || 'Si è verificato un errore';
  }

  // Fallback to other error formats
  if (typeof data.error === 'string') {
    return data.error;
  }

  if (typeof data.message === 'string') {
    return data.message;
  }

  if (data.error_description) {
    if (Array.isArray(data.error_description)) {
      const [firstError] = data.error_description;
      return typeof firstError === 'string' ? firstError : 'Si è verificato un errore';
    }
    if (typeof data.error_description === 'string') {
      return data.error_description;
    }
  }

  return 'Si è verificato un errore sconosciuto';
}

/**
 * Show error toast notification
 * @param message The error message to display
 */
function showErrorToast(message: string): void {
  // Use the global toast function exposed by the Toaster component
  if (typeof window !== 'undefined' && (window as unknown as { addToast?: (toast: unknown) => void }).addToast) {
    (window as unknown as { addToast: (toast: unknown) => void }).addToast({
      title: "Errore",
      description: message,
      variant: "destructive",
    });
  } else {
    // Fallback to console if toast system is not available
    console.error('Error:', message);
  }
}

/**
 * Custom fetch client with language header interceptor and configurable base URL
 */
interface FetchOptions extends RequestInit {
  requireAuth?: boolean;
  showErrors?: boolean; // Per-request override for error display
}

/**
 * The header a helper's phone presents in place of a session (RoundDelegation
 * on the server): `<link token>.<phone secret>`.
 */
export const ROUND_DELEGATION_HEADER = 'X-Round-Delegation';

/**
 * A helper's phone working one round for somebody else (see
 * `components/competition/round-delegation`). While it is set:
 *
 * - every request about that competition goes out **without** the viewer's own
 *   Bearer — the helper may be signed in as themselves, and the two sets of
 *   powers must never travel together (the server ignores a Bearer next to the
 *   header anyway);
 * - the requests the delegation is for — the round itself, its scoreboard and
 *   its results — carry the delegation's header;
 * - anything else in the app (notifications, the profile) is untouched.
 */
export interface RoundDelegationScope {
  /** `<token>.<secret>`. */
  credential: string;
  /** The competition as the pages address it: its slug, and its id. */
  competitionKeys: string[];
  roundId: string;
}

type DelegationTreatment = 'delegated' | 'anonymous' | null;

function pathOf(url: string): string {
  try {
    return new URL(url, 'http://placeholder').pathname;
  } catch {
    return url.split('?')[0];
  }
}

const escapeRegExp = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

class FetchClient {
  private config: ApiClientConfig;
  private isRefreshing = false;
  private refreshQueue: Array<() => void> = [];
  private roundDelegation: RoundDelegationScope | null = null;

  constructor(config?: Partial<ApiClientConfig>) {
    this.config = {
      baseURL: getApiBaseUrl(),
      timeout: 10000,
      showErrors: true,
      ...config
    };
  }

  /**
   * Set the base URL for all API requests
   * @param baseURL The base URL for the API
   */
  setBaseURL(baseURL: string): void {
    this.config.baseURL = baseURL;
  }

  /**
   * Get the current base URL
   */
  getBaseURL(): string {
    return this.config.baseURL;
  }

  /**
   * Configure the API client
   * @param config Configuration options
   */
  configure(config: Partial<ApiClientConfig>): void {
    this.config = { ...this.config, ...config };
  }

  /**
   * Enable or disable automatic error display
   * @param enabled Whether to show error toasts automatically
   */
  setErrorDisplay(enabled: boolean): void {
    this.config.showErrors = enabled;
  }

  /**
   * Check if automatic error display is enabled
   */
  isErrorDisplayEnabled(): boolean {
    return this.config.showErrors ?? true;
  }

  /**
   * Build the full URL by combining base URL with the endpoint
   * @param url The endpoint URL (can be relative or absolute)
   */
  private buildURL(url: string): string {
    // If URL is already absolute, return as is
    if (url.startsWith('http://') || url.startsWith('https://')) {
      return url;
    }

    // If no base URL is configured, return the URL as is (for relative URLs that work with proxy)
    if (!this.config.baseURL) {
      return url;
    }

    // Remove leading slash if present to avoid double slashes
    const endpoint = url.startsWith('/') ? url.slice(1) : url;

    // Ensure base URL doesn't end with slash
    const baseURL = this.config.baseURL.endsWith('/')
      ? this.config.baseURL.slice(0, -1)
      : this.config.baseURL;

    return `${baseURL}/${endpoint}`;
  }
  /**
   * Performs a fetch request with the current language header automatically added
   * @param url The URL to fetch (can be relative or absolute)
   * @param options Fetch options including requireAuth for authenticated requests
   * @returns Fetch response
   */
  /** Sets (or, with null, ends) the helper's-phone mode — see RoundDelegationScope. */
  setRoundDelegation(scope: RoundDelegationScope | null) {
    this.roundDelegation = scope;
  }

  /**
   * How a request is sent while a helper's phone is working a round: with the
   * delegation's header (the round, its scoreboard, its results), anonymously
   * (anything else about that competition), or as usual.
   */
  private delegationTreatment(url: string): DelegationTreatment {
    const scope = this.roundDelegation;
    if (!scope) return null;

    const path = pathOf(url);
    const keys = scope.competitionKeys.filter(Boolean).map(escapeRegExp).join('|');
    if (!keys) return null;
    if (!new RegExp(`^/api/competitions/(${keys})(/|$)`).test(path)) return null;

    const round = `^/api/competitions/(${keys})/rounds/${escapeRegExp(scope.roundId)}`;
    const delegated = new RegExp(`${round}(/scores(/[^/]+)?|/climbs/[^/]+/results(/.*)?)?$`);
    return delegated.test(path) ? 'delegated' : 'anonymous';
  }

  async fetch(url: RequestInfo | URL, options?: FetchOptions): Promise<Response> {
    const { requireAuth = false, showErrors, ...fetchOptions } = options || {};
    const currentLanguage = i18n.language;
    const fullURL = typeof url === 'string' ? this.buildURL(url) : url;

    // Determine if errors should be shown (per-request override or global config)
    const shouldShowErrors = showErrors !== undefined ? showErrors : this.config.showErrors;

    // Prepare headers with language
    const headers: Record<string, string> = {
      'Accept-Language': currentLanguage,
      ...(fetchOptions.headers as Record<string, string> || {})
    };

    // A helper's phone working a round sends no session of its own there.
    const treatment = typeof url === 'string' ? this.delegationTreatment(url) : null;
    if (treatment === 'delegated' && this.roundDelegation) {
      headers[ROUND_DELEGATION_HEADER] = this.roundDelegation.credential;
    }

    // Always add authorization token when available
    const token = treatment ? null : localStorage.getItem(TOKEN_KEY);
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    } else if (requireAuth && !treatment) {
      // Only throw error if authentication is explicitly required
      const error = new Error('No authentication token found');
      if (shouldShowErrors) {
        showErrorToast('Devi effettuare l\'accesso per continuare');
      }
      throw error;
    }

    const updatedOptions: RequestInit = {
      ...fetchOptions,
      headers
    };

    // Make the initial request
    let response = await fetch(fullURL, updatedOptions);

    // Check for unauthorized error (expired token) - attempt refresh if we had a token
    if (response.status === 401 && token) {
      // Check if it's specifically an expired token error
      try {
        const errorData = await response.clone().json();
        const isExpiredToken = errorData.error === 'expired_token' ||
                              (errorData.error_description &&
                               (Array.isArray(errorData.error_description)
                                ? errorData.error_description.some((desc: string) => desc.includes('è scaduto'))
                                : errorData.error_description.includes('è scaduto')));

        if (isExpiredToken) {
          // Try to refresh the token
          const refreshed = await this.refreshAuthToken();

          if (refreshed) {
            // Retry the request with new token
            const newToken = localStorage.getItem(TOKEN_KEY);
            if (newToken) {
              headers['Authorization'] = `Bearer ${newToken}`;
              const retryOptions: RequestInit = {
                ...fetchOptions,
                headers
              };

              response = await fetch(fullURL, retryOptions);
            }
          }
        }
      } catch {
        // If we can't parse the response, still try to refresh on any 401
        const refreshed = await this.refreshAuthToken();

        if (refreshed) {
          // Retry the request with new token
          const newToken = localStorage.getItem(TOKEN_KEY);
          if (newToken) {
            headers['Authorization'] = `Bearer ${newToken}`;
            const retryOptions: RequestInit = {
              ...fetchOptions,
              headers
            };

            response = await fetch(fullURL, retryOptions);
          }
        }
      }
    }

    // Handle error responses and show toast if enabled
    if (!response.ok && shouldShowErrors) {
      try {
        const errorData = await response.clone().json();
        const errorMessage = extractErrorMessage(errorData);

        // Don't show error toast for authentication errors as they're handled above
        if (response.status !== 401) {
          showErrorToast(errorMessage);
        }
      } catch {
        // If we can't parse the error response, show a generic message
        showErrorToast(`Errore ${response.status}: ${response.statusText}`);
      }
    }

    return response;
  }

  /**
   * Attempt to refresh the authentication token
   * @returns Promise resolving to true if token refresh was successful
   */
  async refreshAuthToken(): Promise<boolean> {
    // If already refreshing, queue the request
    if (this.isRefreshing) {
      return new Promise<boolean>((resolve) => {
        this.refreshQueue.push(() => resolve(true));
      });
    }

    this.isRefreshing = true;

    try {
      const refreshTokenValue = localStorage.getItem(REFRESH_TOKEN_KEY);

      if (!refreshTokenValue) {
        return false;
      }

      const response = await refreshAuthToken(refreshTokenValue);

      if (response.token) {
        localStorage.setItem(TOKEN_KEY, response.token);

        // Process queued requests
        this.refreshQueue.forEach(callback => callback());
        this.refreshQueue = [];

        return true;
      }

      return false;
    } catch (error) {
      console.error('Failed to refresh token:', error);
      // Clear tokens when refresh fails
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(REFRESH_TOKEN_KEY);
      return false;
    } finally {
      this.isRefreshing = false;
    }
  }

  /**
   * Shorthand for GET requests
   */
  async get(url: string, options?: FetchOptions): Promise<Response> {
    return this.fetch(url, {
      ...options,
      method: 'GET'
    });
  }

  /**
   * Shorthand for POST requests
   */
  async post(url: string, data: unknown, options?: FetchOptions): Promise<Response> {
    return this.fetch(url, {
      ...options,
      method: 'POST',
      headers: {
        ...options?.headers,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(data)
    });
  }

  /**
   * Shorthand for PUT requests
   */
  async put(url: string, data: unknown, options?: FetchOptions): Promise<Response> {
    return this.fetch(url, {
      ...options,
      method: 'PUT',
      headers: {
        ...options?.headers,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(data)
    });
  }

  /**
   * Shorthand for PATCH requests
   */
  async patch(url: string, data: unknown, options?: FetchOptions): Promise<Response> {
    return this.fetch(url, {
      ...options,
      method: 'PATCH',
      headers: {
        ...options?.headers,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(data)
    });
  }

  /**
   * Shorthand for DELETE requests
   */
  async delete(url: string, options?: FetchOptions): Promise<Response> {
    return this.fetch(url, {
      ...options,
      method: 'DELETE'
    });
  }

  /**
   * Process API response with error handling and JSON parsing
   * @param response Fetch response to process
   */
  async processResponse<T>(response: Response): Promise<{ data?: T; error?: string }> {
    try {
      if (!response.ok) {
        const errorData = await safeParseJSON(response) as Record<string, unknown>;

        // Check if it's an expired token error
        if (response.status === 401 &&
            (errorData.error === 'expired_token' || (errorData.error_description as string)?.includes('è scaduto'))) {
          throw new Error('Token expired');
        }

        const errorDescriptions = errorData.error_description as string[] | undefined;
        throw new Error((errorData.message as string) || errorDescriptions?.[0] ||
                      `Request failed with status: ${response.status}`);
      }

      // Parse successful response
      const data = await safeParseJSON(response) as T;
      return { data };
    } catch (error) {
      return {
        error: error instanceof Error ? error.message : 'An unknown error occurred',
      };
    }
  }

  /**
   * Enhanced fetch method that returns ApiResponse format (for compatibility)
   */
  async fetchWithResponse<T>(url: string, options?: FetchOptions): Promise<ApiResponse<T>> {
    try {
      const response = await this.fetch(url, options);
      return await this.processResponse<T>(response);
    } catch (error) {
      return {
        error: error instanceof Error ? error.message : 'An unknown error occurred',
      };
    }
  }
}

// Create a singleton instance
export const apiClient = new FetchClient();

// Export the class for testing or creating new instances
export { FetchClient };

// Export the ApiResponse interface for typing
export type { ApiResponse };

// For testing purposes, we expose these methods
export const tokenStorage = {
  getToken: () => localStorage.getItem(TOKEN_KEY),
  setToken: (token: string) => localStorage.setItem(TOKEN_KEY, token),
  getRefreshToken: () => localStorage.getItem(REFRESH_TOKEN_KEY),
  setRefreshToken: (token: string) => localStorage.setItem(REFRESH_TOKEN_KEY, token),
  clearTokens: () => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
  }
};

// Also provide the interceptor functionality
const originalFetch = window.fetch;

// Override the global fetch to include language header
window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
  const currentLanguage = i18n.language;

  const updatedInit: RequestInit = {
    ...init,
    headers: {
      ...init?.headers,
      'Accept-Language': currentLanguage
    }
  };

  return originalFetch(input, updatedInit);
};
